// ─────────────────────────────────────────────────────────────────────────────
// spiral.ts — Spiral classifier for closed-ish strokes
//
// A spiral is a stroke whose cumulative heading change exceeds 2π (one full
// turn) and whose distance-from-centroid trends monotonically (in or out).
// We classify a stroke as a spiral when:
//   - total signed heading change >= 2π × 0.85 (i.e. ~0.85 turns)
//   - the per-sample radius from the centroid is monotonic (with a small
//     tolerance) — distinguishing spiral from circle (constant radius).
//
// Pure function. Returns null if the stroke isn't a spiral.
// ─────────────────────────────────────────────────────────────────────────────

import type { Point } from '@shared/notebook_model';
import type { StrokeShape } from './shape_detector';

interface PointWithTs { x: number; y: number; timestamp: number }

const MIN_TURNS         = 0.85;
const MIN_POINTS        = 16;
const RADIUS_MONOTONIC  = 0.65;   // fraction of sequential pairs that must agree on direction

/** Robust centroid (uniform mean of points). */
function centroid(pts: ReadonlyArray<PointWithTs>): Point {
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
}

/** Total signed heading change in radians (positive = ccw, negative = cw),
 *  unwrapping at ±π so a full turn accumulates ±2π. */
function totalHeadingChange(pts: ReadonlyArray<PointWithTs>, c: Point): number {
  let prevAngle = Math.atan2(pts[0].y - c.y, pts[0].x - c.x);
  let sum = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = Math.atan2(pts[i].y - c.y, pts[i].x - c.x);
    let d = a - prevAngle;
    while (d >  Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    sum += d;
    prevAngle = a;
  }
  return sum;
}

/** Fraction of consecutive radius pairs that move in the same direction
 *  (both growing or both shrinking). High value → monotonic radius. */
function radialMonotonicity(pts: ReadonlyArray<PointWithTs>, c: Point): { score: number; direction: -1 | 0 | 1 } {
  const radii: number[] = [];
  for (const p of pts) {
    const dx = p.x - c.x, dy = p.y - c.y;
    radii.push(Math.sqrt(dx * dx + dy * dy));
  }
  let positiveSteps = 0, negativeSteps = 0;
  for (let i = 1; i < radii.length; i++) {
    const d = radii[i] - radii[i - 1];
    if (d > 0.3) positiveSteps++;
    else if (d < -0.3) negativeSteps++;
  }
  const total = Math.max(positiveSteps + negativeSteps, 1);
  const grows  = positiveSteps / total;
  const shrinks = negativeSteps / total;
  if (grows >= RADIUS_MONOTONIC) return { score: grows, direction: 1 };
  if (shrinks >= RADIUS_MONOTONIC) return { score: shrinks, direction: -1 };
  return { score: Math.max(grows, shrinks), direction: 0 };
}

export function detectSpiral(pts: ReadonlyArray<PointWithTs>): StrokeShape | null {
  if (pts.length < MIN_POINTS) return null;
  const c = centroid(pts);
  const head = totalHeadingChange(pts, c);
  const turns = Math.abs(head) / (2 * Math.PI);
  if (turns < MIN_TURNS) return null;
  const mono = radialMonotonicity(pts, c);
  if (mono.direction === 0) return null;
  return {
    kind: 'spiral',
    center: c,
    turns,
    sense: head > 0 ? 'ccw' : 'cw',
    radiusGrowth: mono.direction,
  };
}
